import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, Plus, Trash2, X, Loader2, RefreshCw, Cloud, CloudOff, AlertCircle } from 'lucide-react';
import { subtasksApi, Subtask } from '../../api/client';
import { cn } from '../../utils/cn';
import { toast } from 'sonner';

interface SubtaskListProps {
  accountId: string;
  messageId: string;
  provider: string;
  todoLinkExists: boolean;
}

export function SubtaskList({ accountId, messageId, provider, todoLinkExists }: SubtaskListProps) {
  const [isAdding, setIsAdding] = useState(false);
  const queryClient = useQueryClient();
  
  // Only fetch subtasks for Microsoft accounts with a todoLink
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['subtasks', accountId, messageId],
    queryFn: () => subtasksApi.getSubtasks(accountId, messageId),
    enabled: provider === 'microsoft' && todoLinkExists,
    staleTime: 10 * 1000,
    refetchInterval: 30 * 1000, // Sync every 30 seconds
  });
  
  const subtasks = data?.subtasks || [];
  
  // Sync mutation
  const syncMutation = useMutation({
    mutationFn: () => subtasksApi.syncSubtasks(accountId, messageId),
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', accountId, messageId] });
      toast.success(result.message);
    },
    onError: () => {
      toast.error('Sync fehlgeschlagen');
    },
  });
  
  // Don't show for non-Microsoft accounts or if no todoLink exists
  if (provider !== 'microsoft' || !todoLinkExists) {
    return null;
  }
  
  if (isLoading) {
    return (
      <div className="flex items-center gap-2 py-2">
        <Loader2 className="w-3 h-3 animate-spin" style={{ color: 'var(--text-tertiary)' }} />
        <span className="text-xs" style={{ color: 'var(--text-tertiary)' }}>Lade Subtasks...</span>
      </div>
    );
  }
  
  return (
    <div className="space-y-1">
      {/* Header with sync button */}
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium" style={{ color: 'var(--text-secondary)' }}>
          Subtasks ({subtasks.length})
        </span>
        <button
          onClick={() => syncMutation.mutate()}
          disabled={syncMutation.isPending}
          className="flex items-center gap-1 px-2 py-1 text-xs rounded transition-colors"
          style={{ color: 'var(--text-tertiary)' }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--brand-primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--text-tertiary)'}
          title="Mit Microsoft To-Do synchronisieren"
        >
          {syncMutation.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <RefreshCw className="w-3 h-3" />
          )}
        </button>
      </div>
      
      {/* Subtask list */}
      {subtasks.map((subtask) => (
        <SubtaskItem
          key={subtask.id}
          subtask={subtask}
          accountId={accountId}
          messageId={messageId}
        />
      ))}
      
      {/* Add new subtask */}
      {isAdding ? (
        <AddSubtaskInput
          accountId={accountId}
          messageId={messageId}
          onCancel={() => setIsAdding(false)}
          onSuccess={() => setIsAdding(false)}
        />
      ) : (
        <button
          onClick={(e) => {
            e.stopPropagation();
            setIsAdding(true);
          }}
          className="flex items-center gap-1.5 px-2 py-1.5 text-xs rounded-md transition-colors w-full"
          style={{ color: 'var(--text-tertiary)' }}
          onMouseEnter={e => {
            e.currentTarget.style.backgroundColor = 'var(--bg-hover)';
            e.currentTarget.style.color = 'var(--brand-primary)';
          }}
          onMouseLeave={e => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.color = 'var(--text-tertiary)';
          }}
        >
          <Plus className="w-3 h-3" />
          <span>Subtask hinzufügen</span>
        </button>
      )}
    </div>
  );
}

interface SubtaskItemProps {
  subtask: Subtask;
  accountId: string;
  messageId: string;
}

function SubtaskItem({ subtask, accountId, messageId }: SubtaskItemProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(subtask.displayName);
  const [isHovered, setIsHovered] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  
  const updateMutation = useMutation({
    mutationFn: (data: { displayName?: string; isChecked?: boolean }) => 
      subtasksApi.updateSubtask(accountId, subtask.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', accountId, messageId] });
    },
    onError: () => {
      toast.error('Subtask konnte nicht aktualisiert werden');
    },
  });
  
  const deleteMutation = useMutation({
    mutationFn: () => subtasksApi.deleteSubtask(accountId, subtask.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', accountId, messageId] });
    },
    onError: () => {
      toast.error('Subtask konnte nicht gelöscht werden');
    },
  });
  
  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);
  
  const handleToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    updateMutation.mutate({ isChecked: !subtask.isChecked });
  };
  
  const handleSaveEdit = () => {
    if (editName.trim() && editName !== subtask.displayName) {
      updateMutation.mutate({ displayName: editName.trim() });
    }
    setIsEditing(false);
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveEdit();
    } else if (e.key === 'Escape') {
      setEditName(subtask.displayName);
      setIsEditing(false);
    }
  };
  
  // Sync status icon
  const SyncIcon = subtask.syncStatus === 'synced' ? Cloud 
    : subtask.syncStatus === 'error' ? AlertCircle 
    : CloudOff;
  const syncColor = subtask.syncStatus === 'synced' ? 'var(--color-success)' 
    : subtask.syncStatus === 'error' ? 'var(--color-error)' 
    : 'var(--text-tertiary)';
  
  return (
    <div 
      className={cn(
        'group flex items-center gap-2 px-2 py-1.5 rounded-md transition-colors text-sm'
      )}
      style={{ 
        backgroundColor: isHovered ? 'var(--bg-hover)' : 'transparent',
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={e => e.stopPropagation()}
    >
      {/* Checkbox */}
      <button
        onClick={handleToggle}
        className={cn(
          'flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center transition-all',
          subtask.isChecked 
            ? 'border-emerald-500 bg-emerald-500' 
            : 'border-gray-400 hover:border-emerald-500'
        )}
        disabled={updateMutation.isPending}
      >
        {subtask.isChecked && <Check className="w-3 h-3 text-white" />}
      </button>
      
      {/* Title */}
      {isEditing ? (
        <input
          ref={inputRef}
          type="text"
          value={editName}
          onChange={e => setEditName(e.target.value)}
          onBlur={handleSaveEdit}
          onKeyDown={handleKeyDown}
          className="flex-1 bg-transparent outline-none px-1 py-0.5 rounded text-sm"
          style={{ 
            color: 'var(--text-primary)',
            border: '1px solid var(--brand-primary)',
          }}
          onClick={e => e.stopPropagation()}
        />
      ) : (
        <span 
          className={cn(
            'flex-1 truncate cursor-text',
            subtask.isChecked && 'line-through'
          )}
          style={{ 
            color: subtask.isChecked ? 'var(--text-disabled)' : 'var(--text-primary)' 
          }}
          onDoubleClick={() => setIsEditing(true)}
        >
          {subtask.displayName}
        </span>
      )}
      
      {/* Sync status indicator */}
      <span
        title={
          subtask.syncStatus === 'synced' ? 'Mit Microsoft synchronisiert' 
          : subtask.syncStatus === 'error' ? `Sync-Fehler: ${subtask.lastSyncError}` 
          : 'Nicht synchronisiert'
        }
      >
        <SyncIcon 
          className="w-3 h-3 flex-shrink-0" 
          style={{ color: syncColor }}
        />
      </span>
      
      {/* Delete button */}
      {isHovered && !isEditing && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            deleteMutation.mutate();
          }}
          className="flex-shrink-0 p-1 rounded transition-colors opacity-60 hover:opacity-100"
          style={{ color: 'var(--color-error)' }}
          disabled={deleteMutation.isPending}
        >
          {deleteMutation.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Trash2 className="w-3 h-3" />
          )}
        </button>
      )}
    </div>
  );
}

interface AddSubtaskInputProps {
  accountId: string;
  messageId: string;
  onCancel: () => void;
  onSuccess: () => void;
}

function AddSubtaskInput({ accountId, messageId, onCancel, onSuccess }: AddSubtaskInputProps) {
  const [name, setName] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const queryClient = useQueryClient();
  
  const createMutation = useMutation({
    mutationFn: () => subtasksApi.createSubtask(accountId, messageId, name.trim()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['subtasks', accountId, messageId] });
      setName('');
      onSuccess();
    },
    onError: () => {
      toast.error('Subtask konnte nicht erstellt werden');
    },
  });
  
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  
  const handleSubmit = () => {
    if (name.trim()) {
      createMutation.mutate();
    }
  };
  
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSubmit();
    } else if (e.key === 'Escape') {
      onCancel();
    }
  };
  
  return (
    <div 
      className="flex items-center gap-2 px-2 py-1.5"
      onClick={e => e.stopPropagation()}
    >
      <div 
        className="flex-shrink-0 w-4 h-4 rounded border-2 flex items-center justify-center"
        style={{ borderColor: 'var(--border-default)' }}
      />
      <input
        ref={inputRef}
        type="text"
        value={name}
        onChange={e => setName(e.target.value)}
        onKeyDown={handleKeyDown}
        onBlur={() => {
          if (!name.trim()) onCancel();
        }}
        placeholder="Neuer Subtask..."
        className="flex-1 bg-transparent outline-none text-sm px-1 py-0.5"
        style={{ color: 'var(--text-primary)' }}
        disabled={createMutation.isPending}
      />
      {name.trim() && (
        <button
          onClick={handleSubmit}
          className="p-1 rounded transition-colors"
          style={{ color: 'var(--brand-primary)' }}
          disabled={createMutation.isPending}
        >
          {createMutation.isPending ? (
            <Loader2 className="w-3 h-3 animate-spin" />
          ) : (
            <Check className="w-3 h-3" />
          )}
        </button>
      )}
      <button
        onClick={onCancel}
        className="p-1 rounded transition-colors"
        style={{ color: 'var(--text-tertiary)' }}
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
}
